<template>

  <v-container fluid class="ma-0 pa-0 route-order-manager">

    <v-row no-gutters class="mb-6">
      <v-col cols="12" align="center">
        <v-btn
            small
            outlined
            :disabled="!isRouteEditable"
            class="mr-12 btn-mode"
            :class="{ activeMode: isManualMode }"
            @click="switchToMode('manual')">
          Manual Mode
        </v-btn>
        <v-btn
            small
            outlined
            :disabled="!isRouteEditable"
            class="btn-mode"
            :class="{ activeMode: !isManualMode }"
            @click="switchToMode('automatic')">
          Automatic Mode
        </v-btn>
      </v-col>
    </v-row>

    <v-row no-gutters v-if="errorMessage">
      <v-col cols="12" align="center">
        <div class="error-container">
          {{ errorMessage }}
        </div>
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <!----------------------------------------->
      <!-- Generate Route button & Order count -->
      <!----------------------------------------->
      <v-col cols="8" xs="6" sm="8" md="8" lg="8" class="ma-0 pa-0 pl-4 d-flex align-center">
        <!-- Generate route button -->
        <v-btn
            v-if="showGenerateButton"
            large
            class="mr-6 mb-1 px-10"
            color="primary"
            :disabled="!scheduledStartTime || !regenerateRouteRequired || isMapDataLoading"
            @click="handleGenerateRoute">
            {{ generateButtonText }}
        </v-btn>
        <!-- Order count -->
        <span class="order-count-text">Orders ({{ deliverablOrderCount }})</span>
        <!-- Coordinates count -->
        <span class="pl-8 coords-count-text">Coords: {{ coordsCount }}</span>
      </v-col>

      <!---------------------------------------------->
      <!-- Add Order action & Order Selector dialog -->
      <!---------------------------------------------->
      <v-col cols="4" xs="4" sm="4" md="4" lg="4" class="ma-0 pa-0 d-flex align-center justify-end">
        <v-dialog scrollable persistent max-width="980px" v-model="isOrderSelectorOpen" v-if="permitAddOrders">
          <!-- Activator button -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn plain color="cyan accent-3" class="pr-10" v-bind="attrs" v-on="on">
              + Add Order
            </v-btn>
          </template>
          <route-order-selector
              :testMode="route.is_test"
              v-on:canceled="isOrderSelectorOpen = false"
              v-on:selected="handleAddOrder" />
        </v-dialog>
      </v-col>
    </v-row>

    <!---------------------------------------------------->
    <!-- Undeliverable Orders (when dispatched editing) -->
    <!---------------------------------------------------->
    <div class="order-list mt-3 mb-3" v-if="unDeliverableOrders.length > 0">
      <v-row>
        <v-col cols="12" class="d-flex align-center">
          <div class="pl-2 undeliverable-order-count-text">Rescheduled Orders <span class="pl-1">({{ unDeliverableOrders.length }})</span></div>
          <v-btn plain icon color="white" class="ml-2" @click="showUndeliverableOrders = !showUndeliverableOrders">
            <v-icon style="font-size:28px;" v-if="!showUndeliverableOrders">mdi-chevron-down</v-icon>
            <v-icon style="font-size:28px;" v-else>mdi-chevron-up</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <transition name="slide-down" appear>
        <div v-if="showUndeliverableOrders" style="margin-top:10px;">
          <route-builder-undeliverable-order-card
              v-for="order in unDeliverableOrders"
              class="mb-4"
              :key="`undeliverable-${order.id}`"
              :order="order" />
        </div>
      </transition>
    </div>

    <!------------------------------>
    <!-- Sortable Order Card List -->
    <!------------------------------>
    <div class="pt-4 order-list">

      <!-- (data loading) -->
      <v-row v-if="isDataLoading">
        <v-col align="center" class="py-8">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>

      <template v-else>
        <!----------------->
        <!-- Manual Mode -->
        <!----------------->
        <template v-if="isManualMode">
          <v-row v-if="sortedLocations.length === 0">
            <v-col align="center" class="py-8">
              <dialog-confirm-button
                  buttonText="Delete Route"
                  buttonColor="red"
                  message="Are you sure you want to delete this Route?"
                  icon="mdi-trash-can"
                  iconColor="white"
                  @confirmed="emitDeleteRoute" />
            </v-col>
          </v-row>
          <draggable v-model="sortedLocations" @start="handleDragStart" @end="handleMove" draggable=".item-sortable">
            <transition-group>
              <route-builder-order-location-card
                  v-for="(locData, index) in sortedLocations"
                  class="mb-4"
                  :class="(canMoveInReorder(locData)) ? 'item-sortable' : 'item-blocked'"
                  :style="(canMoveInReorder(locData)) ? 'cursor:grab;' : 'cursor:not-allowed;'"
                  :key="`loc-${index}`"
                  :location="locData.loc"
                  :order="locData.order"
                  :locationIndex="index+1"
                  :showRemove="permitRemoveOrders && locData.order.order_status === 'BEING_SCHEDULED'"
                  @dirty="emitDirty"
                  @removeOrder="handleRemoveOrder" />
            </transition-group>
          </draggable>
        </template>

        <!-------------------->
        <!-- Automatic Mode -->
        <!-------------------->
        <template v-else>
          <v-row v-if="sortedOrders.length === 0">
            <v-col align="center" class="py-8">
              <dialog-confirm-button
                  buttonText="Delete Route"
                  buttonColor="red"
                  message="Are you sure you want to delete this Route?"
                  icon="mdi-trash-can"
                  iconColor="white"
                  @confirmed="emitDeleteRoute" />
            </v-col>
          </v-row>
          <route-builder-order-card
              v-else
              v-for="(order, index) in sortedOrders"
              class="mb-4"
              :key="`order-${index}`"
              :order="order"
              :orderIndex="index+1"
              :showRemove="permitRemoveOrders && order.order_status === 'BEING_SCHEDULED'"
              @dirty="emitDirty"
              @removeOrder="handleRemoveOrder" />
        </template>
      </template>

    </div> <!-- END order-list -->

  </v-container>

</template>

<script>
import Draggable from 'vuedraggable'
import RouteBuilderUndeliverableOrderCard from '@/components/routes/RouteBuilderUndeliverableOrderCard'
import RouteBuilderOrderCard from '@/components/routes/RouteBuilderOrderCard'
import RouteBuilderOrderLocationCard from '@/components/routes/RouteBuilderOrderLocationCard'
import RouteOrderSelector from '@/components/routes/RouteOrderSelector'
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'RouteOrderManager',

  components: {
    Draggable,
    RouteBuilderUndeliverableOrderCard,
    RouteBuilderOrderCard,
    RouteBuilderOrderLocationCard,
    RouteOrderSelector,
    DialogConfirmButton
  },

  props: {
    route: {
      type: Object,
      required: true
    },
    isRouteEditable: {
      type: Boolean,
      required: true
    },
    orders: {
      type: Array,
      default: null
    },
    isManualMode: {
      type: Boolean,
      default: true
    },
    permitAddOrders: {
      type: Boolean,
      default: true
    },
    permitRemoveOrders: {
      type: Boolean,
      default: true
    },
    generateButtonText: {
      type: String,
      default: 'Generate Route'
    }
  },

  data () {
    return {
      isDataLoading: false,
      sortedLocations: [],
      deliverableOrders: [],
      unDeliverableOrders: [],
      showUndeliverableOrders: false,
      isOrderSelectorOpen: false,
      errorMessage: null,
      drag: false,
      coordsCount: 0
    }
  },

  computed: {
    // ---------------------------- Component settings
    showGenerateButton () {
      return this.isManualMode && this.isRouteEditable
    },
    regenerateRouteRequired () {
      return this.$parent.regenerateRouteRequired
    },
    isMapDataLoading () {
      return this.$parent.isMapDataLoading
    },
    // ---------------------------- Route info
    hasDispatched () {
      return this.route.route_status !== 'CREATED'
    },
    scheduledStartTime () {
      return this.route.scheduled_start_time
    },
    // ---------------------------- Order info
    deliverablOrderCount () {
      return (this.deliverableOrders) ? this.deliverableOrders.length : 0
    },
    // The proper sort for the dropoffs (per the built route_info.steps)
    sortForDropoffs () {
      const routeSteps = this.route.route_info.steps || []
      return routeSteps
        .filter(step => step.type === 'dropoff' && Array.isArray(step.order_ids))
        .reduce((acc, step) => acc.concat(step.order_ids), [])
    },
    sortedOrders () {
      return (this.deliverableOrders)
        ? this.$root.utils.orderBy(this.deliverableOrders, order => order.estimated_receiver_arrival_time, ['asc'])
        : []
    }
  },

  // TODO - Add watch (route) ???

  async beforeMount () {
    await this.extractLocations()
    this.calculateCoordsCount()
  },

  methods: {
    switchToMode (mode) {
      this.$emit('switchMode', mode)
      this.calculateCoordsCount() // calculate coords
    },

    handleAddOrder (order) {
      this.calculateCoordsCount() // calculate coords
      this.emitOrderAdd(order)
      this.isOrderSelectorOpen = false
    },

    handleRemoveOrder (order) {
      this.calculateCoordsCount() // calculate coords
      this.emitOrderRemove(order)
    },

    emitDirty (property, order) {
      this.$emit('dirty', property, order)
    },

    emitOrderAdd (order) {
      this.$emit('orderAdd', order)
    },

    emitOrderRemove (order) {
      this.$emit('orderRemove', order)
    },

    canMoveInReorder (locData = {}) {
      return locData.type === 'dropoff-location'
    },

    handleDragStart () {
      this.drag = true
    },

    // Handle order move (re-ordering)
    handleMove (event) {
      this.drag = false // reset drag state
      this.calculateCoordsCount() // calculate coords

      // Emit the moved order
      const movedLocation = this.sortedLocations[event.newIndex]
      this.$emit('orderMoved', movedLocation)
    },

    // Determine number of coordinate pairs that will be processed by Mapbox
    calculateCoordsCount () {
      if (this.sortedLocations.length === 0) {
        this.coordsCount = 0
      } else {
        const normalizedCoords = this.sortedLocations
          .map(it => [it.loc.coords.longitude, it.loc.coords.latitude])
          // Filter out consecutive duplicate coordinates
          .filter((coord, index, array) => {
            if (index === 0) return true
            const prev = array[index - 1]
            return prev[0] !== coord[0] || prev[1] !== coord[1]
          })
        this.coordsCount = normalizedCoords.length + 2
      }
    },

    handleGenerateRoute () {
      this.errorMessage = null

      // Manual mode
      if (this.isManualMode) {
        if (this.validateLocations()) this.$emit('generateRoute', this.sortedLocations)
        else this.$emit('error', this.errorMessage)
      // Automatic mode
      } else {
        this.$emit('generateRoute')
      }
    },

    emitDeleteRoute () {
      this.$emit('deleteRoute')
    },

    // Validate locations are in appropriate sort for route generation (manual mode only)
    validateLocations () {
      let result = true

      const pickups = []
      for (const loc of this.sortedLocations) {
        const orderId = (loc.order) ? loc.order.id : ''
        const orderNum = (loc.order) ? loc.order.order_number : ''

        // Ensure a pickup precedes a dropoff
        if (loc.type === 'pickup-location') pickups.push(orderId)
        if (loc.type === 'dropoff-location') {
          if (!this.$root.utils.includes(pickups, orderId)) {
            result = false
            this.errorMessage = `${orderNum} must be picked-up before dropoff.`
          }
        }
      }

      return result
    },

    // Extract locations for manual control
    extractLocations () {
      if (!this.orders) return false

      this.isDataLoading = true

      // Filter out rescheduled orders
      this.unDeliverableOrders = this.orders.filter(it => it.order_status === 'RESCHEDULED')
      this.deliverableOrders = this.orders.filter(it => it.order_status !== 'RESCHEDULED')

      // Build pickup locations array
      const pickupLocations = []
      for (const order of this.deliverableOrders) {
        pickupLocations.push({
          type: 'pickup-location',
          loc: order.shipper,
          order,
          estimated_arrival_time: order.estimated_shipper_arrival_time
        })
      }

      // Cluster pickups by location
      const pickupClusters = {}
      for (const pickup of pickupLocations) {
        const shipperId = pickup.order.shipper_id
        if (!pickupClusters[shipperId]) {
          pickupClusters[shipperId] = []
        }
        pickupClusters[shipperId].push(pickup)
      }

      // Sort pickups by estimated arrival time
      const sortedPickupLocations = Object.values(pickupClusters)
        .flat()
        .sort((a, b) => new Date(a.estimated_arrival_time) - new Date(b.estimated_arrival_time))

      // Build dropoff locations array
      const dropoffLocations = []
      for (const order of this.deliverableOrders) {
        dropoffLocations.push({
          type: 'dropoff-location',
          loc: order.receiver,
          order,
          estimated_arrival_time: order.estimated_receiver_arrival_time
        })
      }

      // Sort dropoffs by estimated arrival time
      const sortedDropoffLocations = this.$root.utils.orderBy(dropoffLocations, ['estimated_arrival_time'], ['asc'])

      // TODO - This stops working if you add or remove an order, because the steps haven't generated yet !!!
      // const sortedByArrival = this.$root.utils.orderBy(dropoffLocations, ['estimated_arrival_time'], ['asc'])
      // const sortedDropoffLocations = this.sortForDropoffs
      //   .map(orderId => sortedByArrival.find(loc => loc.order.id === orderId))
      //   .filter(Boolean) // Remove any undefined in case of missing matches

      // Combine for order manager
      const deliverableLocations = [...sortedPickupLocations, ...sortedDropoffLocations]
      this.sortedLocations = deliverableLocations

      this.isDataLoading = false
    }
  }
}
</script>

<style>
  .route-order-manager .btn-mode {
    border: 1px solid #616161;
    color: #616161;
  }
  .route-order-manager .btn-mode:not(.activeMode):hover {
    border: 1px solid #FFB300;
    color: #FFB300;
  }

  .route-order-manager .activeMode {
    border: 1px solid #FFB300;
    color: #FFB300;
  }
  .route-order-manager .activeMode .v-btn__content {
    color: #FFB300 !important;
  }

  .route-order-manager .undeliverable-order-count-text {
    font-size: 1.18em;
  }

  .route-order-manager .order-count-text {
    font-size: 1.28em;
  }

  .route-order-manager .coords-count-text {
    font-size: 1.0em;
    color: #727272;
  }

  .route-order-manager .error-container {
    border: 1px solid #C62828;
    border-radius: 6px;
    color: #C62828;
    max-width: 490px;
    padding: 0.8rem 1.1rem;
  }

  .route-order-manager .order-list {
    border: 1px solid #252525;
    padding: 8px 10px;
  }

  /* Slide down transition */
  .slide-down-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-down-leave-active {
    transition: all 0.3s ease-in;
  }

  .slide-down-enter-from,
  .slide-down-leave-to {
    transform: translateY(-20px);
    opacity: 0;
  }
</style>
